import { createApi } from "@reduxjs/toolkit/query/react";
import { apiFunction } from "../utils";
import { baseQueryWithReauth } from "../baseQueryWithReauth";
import { serializeArgsWithoutToken } from "../serializeQueryArgs";

const usersApi = createApi({
  reducerPath: "users",
  baseQuery: baseQueryWithReauth,
  serializeQueryArgs: serializeArgsWithoutToken, // #142: token önbellek anahtarına girmez
  tagTypes: ["User", "EndUser", "EndUserWallet"],
  keepUnusedDataFor: 120,
  endpoints(builder) {
    return {
      addUser: builder.mutation({
        query: ({ input, token }) => ({
            url: "/api/users/storageRoles/createUser",
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: { ...input },
          }),
        invalidatesTags: ["User"],
      }),

      deleteUserByID: builder.mutation({
        query: ({ input, token }) => ({
            url: `/api/users/storageRoles/deleteUser`,
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
            body: { ...input },
          }),
        invalidatesTags: ["User"],
      }),
      updateUser: builder.mutation({
        query: ({ input, token }) => ({
            url: `/api/users/storageRoles/updateUser`,
            method: "PATCH",
            headers: { Authorization: `Bearer ${token}` },
            body: { ...input },
          }),
        invalidatesTags: ["User"],
      }),
      fetchUser: builder.query({
        query: (token) => ({
            url: "/api/users/storageRoles/getUsers",
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: {},
          }),
        providesTags: ["User"],
      }),
      searchUsers: builder.query({
        query: ({ token, search, name, email, firmName, subRole, role, page, limit }) => ({
          url: "/api/users/storageRoles/searchUsers",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { search, name, email, firmName, subRole, role, page, limit },
        }),
        providesTags: ["User"],
      }),
      resendInvitation: builder.mutation({
        query: ({ userId, token }) => ({
          url: "/api/users/storageRoles/resend-invitation",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { id: userId },
        }),
        invalidatesTags: ["User"],
      }),
      getUser: builder.query({
        query: (data) => {
          const { token, ...filteredData } = data;
          return {
            url: `/api/users/storageRoles/getUser`,
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: { ...filteredData },
          };
        },
      }),
      // Kullanıcı son hareketleri (#236) — imleç sayfalama; sayfaları bileşen
      // biriktirir (sonsuz kaydırma), önbellek tutulmaz.
      getUserActivity: builder.query({
        query: (data) => {
          const { token, ...filteredData } = data;
          return {
            url: `/api/users/storageRoles/getUserActivity`,
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: { ...filteredData },
          };
        },
        keepUnusedDataFor: 0,
      }),
      // Son İşlemler (#412) — kullanıcının KENDİ işlemleri (imleçli)
      getMyActivity: builder.query({
        query: (data) => apiFunction(data, "/api/users/storageRoles/getMyActivity", "POST"),
        keepUnusedDataFor: 0,
      }),
      getMyActivityDetail: builder.query({
        query: (data) => apiFunction(data, "/api/users/storageRoles/getMyActivityDetail", "POST"),
        keepUnusedDataFor: 60,
      }),
      fetchPlayMaker: builder.query({
        query: (token) => {
          return {
            url: "/api/users/storageRoles/playmakers",
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          };
        },
        providesTags: ["User"],
      }),
      // #323: verilen liste aynen kaydedilir (boş liste: tüm atamalar kalkar)
      giveDeviceToMachineWorker: builder.mutation({
        query: (input) => {
          return apiFunction(
            input,
            "/api/users/storageRoles/giveDeviceToMachineWorker",
            "POST"
          );
        },
      }),
      // #323: "Cihaz Ata" penceresinin atanmış makine listesi
      getMachineWorkerDevices: builder.query({
        query: (input) =>
          apiFunction(input, "/api/users/storageRoles/getMachineWorkerDevices", "POST"),
        keepUnusedDataFor: 0,
      }),
      getAvailablePermissions: builder.query({
        query: ({ token, id }) => ({
          url: `/api/users/storageRoles/${id}/available-permissions`,
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }),
        providesTags: (result, error, { id }) => [{ type: "User", id }],
      }),
      updateUserPermissions: builder.mutation({
        query: ({ token, id, permissions }) => ({
          url: `/api/users/storageRoles/${id}/permissions`,
          method: "PATCH",
          headers: { Authorization: `Bearer ${token}` },
          body: { permissions },
        }),
        invalidatesTags: ["User"],
      }),

      // ─── EndUser (Çalışan) Endpoints ───
      getEndUsersByFirm: builder.query({
        query: ({ token, firmId }) => ({
          url: "/api/users/storageRoles/endusers/getEndUsersByFirm",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { firmId },
        }),
        providesTags: ["EndUser"],
      }),
      searchUnlinkedEndUsers: builder.mutation({
        query: ({ token, search }) => ({
          url: "/api/users/storageRoles/endusers/searchUnlinkedEndUsers",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { search },
        }),
      }),
      linkEndUserToFirm: builder.mutation({
        query: ({ token, userId, firmId, employeeTemplateId }) => ({
          url: "/api/users/storageRoles/endusers/linkEndUserToFirm",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { userId, firmId, employeeTemplateId },
        }),
        invalidatesTags: ["EndUser"],
      }),
      unlinkEndUserFromFirm: builder.mutation({
        query: ({ token, userId }) => ({
          url: "/api/users/storageRoles/endusers/unlinkEndUserFromFirm",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { userId },
        }),
        invalidatesTags: ["EndUser"],
      }),
      updateEndUserQuota: builder.mutation({
        query: ({ token, userId, periodOptions, periodicWalletRemaining }) => ({
          url: "/api/users/storageRoles/endusers/updateEndUserQuota",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { userId, periodOptions, periodicWalletRemaining },
        }),
        invalidatesTags: ["EndUser"],
      }),

      // ─── Son kullanıcı cüzdanı + Okkalira yükleme (#397) ───
      // Sözleşme: Workspace docs/api-contracts/enduser-wallet-panel.md
      getEndUserWallet: builder.query({
        query: ({ token, userId, limit }) => ({
          url: "/api/users/storageRoles/endusers/getEndUserWallet",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { userId, ...(limit ? { limit } : {}) },
        }),
        providesTags: (result, error, { userId }) => [{ type: "EndUserWallet", id: userId }],
      }),
      grantEndUserOkkalira: builder.mutation({
        query: ({ token, userId, amount, note }) => ({
          url: "/api/users/storageRoles/endusers/grantEndUserOkkalira",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { userId, amount, ...(note ? { note } : {}) },
        }),
        invalidatesTags: (result, error, { userId }) => [{ type: "EndUserWallet", id: userId }],
      }),
    };
  },
});

export const {
  useLazyFetchUserQuery,
  useFetchUserQuery,
  useLazySearchUsersQuery,
  useFetchPlayMakerQuery,
  useGiveDeviceToMachineWorkerMutation,
  useLazyGetMachineWorkerDevicesQuery,
  useAddUserMutation,
  useGetUserQuery,
  useLazyGetUserQuery,
  useLazyGetUserActivityQuery,
  useLazyGetMyActivityQuery,
  useGetMyActivityDetailQuery,
  useUpdateUserMutation,
  useDeleteUserByIDMutation,
  useResendInvitationMutation,
  useGetAvailablePermissionsQuery,
  useUpdateUserPermissionsMutation,
  useGetEndUsersByFirmQuery,
  useLazyGetEndUsersByFirmQuery,
  useSearchUnlinkedEndUsersMutation,
  useLinkEndUserToFirmMutation,
  useUnlinkEndUserFromFirmMutation,
  useUpdateEndUserQuotaMutation,
  useGetEndUserWalletQuery,
  useGrantEndUserOkkaliraMutation,
} = usersApi;
export { usersApi };
